import { dateToEpochSeconds, epochSecondsToDate, stripUndefined, z } from '@customerio/hydra-validation';
import type {
  Snippet as ApiSnippet,
  SnippetsSnippetUsageSummary,
  SnippetsSnippetWithUsage,
  TemplateItem,
} from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Snippet entity — workspace-scoped liquid snippets.
 *
 * **List/detail shape divergence.** The list response carries only the
 * core fields (`id`, `name`, `value`, `updated_at`). The detail and usage
 * endpoints add `message_item_ids` (sideloaded `TemplateItem` ids) and
 * `in_use`, with a top-level `usage_loaded` flag the Ember adapter
 * historically folded onto the snippet. We mirror that here: the extra
 * fields live on `Snippet` as optional, populated when detail/usage land
 * and absent on rows that only came through the list endpoint.
 *
 * **`id` is `string`** per the layer-wide convention even though the wire
 * encodes it as `number` — same number→string coercion as Tag /
 * SubscriptionTopic. Mutations send it back as a number via the write
 * schema.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

export type SnippetMessageItem = TemplateItem;

export type Snippet = Omit<ApiSnippet, 'id' | 'updated_at'> & {
  id: string;
  updated_at: Date;
  /** Sideloaded template-item ids — only present after detail/usage fetches. */
  message_item_ids?: string[];
  /** Whether the snippet is referenced by any template. Detail/usage only. */
  in_use?: boolean;
  /** Whether the detail endpoint completed its usage lookup within the timeout. */
  usage_loaded?: boolean;
};

type SnippetWireWritePayload = Partial<ApiSnippet>;

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

const SnippetFromApi = z
  .looseObject({
    id: z.number().transform(String),
    updated_at: epochSecondsToDate,
    // Declared explicitly so a future schema tightening (e.g. switching
    // away from `looseObject`) doesn't silently drop the top-level
    // `usage_loaded` folded onto the snippet by `parseSnippetDetailResponse`.
    usage_loaded: z.boolean().optional(),
  })
  .pipe(z.custom<Snippet>());

const SnippetToApi = z
  .looseObject({
    id: z.string().transform(Number).pipe(z.number().finite()).optional(),
    updated_at: dateToEpochSeconds.optional(),
  })
  .transform(stripUndefined)
  .pipe(z.custom<SnippetWireWritePayload>());

/* ------------------------------------------------------------------------- */
/* List params                                                                */
/* ------------------------------------------------------------------------- */

export interface SnippetListParams {
  page: number;
  limit?: number;
  search?: string;
  sort?: 'updated_at' | 'name' | 'value';
  asc?: boolean;
}

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export const snippet = defineEntity<Snippet, SnippetWireWritePayload, SnippetListParams, undefined, 'workspace'>({
  name: 'snippet',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SnippetFromApi, write: SnippetToApi },
});

export const snippetKeys = {
  ...snippet.keys,
  /**
   * Detail cache slot that includes sideloaded usage (`message_items`,
   * `usage_loaded`). Single source of truth shared by
   * `getSnippetQueryOptions` and `useUpdateSnippet`'s cache patch — if
   * either side rebuilt the suffix independently, the two could drift
   * and the post-save patch would silently miss.
   */
  detailWithUsage: (workspaceId: number, snippetId: string) =>
    [...snippet.keys.detail(workspaceId, snippetId), 'with-usage'] as const,
  /** One page of where a snippet is used. */
  usage: (workspaceId: number, snippetId: string, continuation: number) =>
    [...snippet.keys.detail(workspaceId, snippetId), 'usage', continuation] as const,
  /**
   * Snippets for a set of object types. Nested under `lists(...)` so snippet
   * mutations' prefix invalidation clears this filtered cache too.
   */
  byObjectType: (workspaceId: number, objectTypeIds: readonly number[]) =>
    [...snippet.keys.lists(workspaceId), 'by-object-type', [...objectTypeIds].sort((a, b) => a - b)] as const,
};

export function parseSnippet(raw: ApiSnippet | SnippetsSnippetWithUsage): Snippet {
  return snippet.parse(raw);
}

/**
 * Parse the detail-endpoint snippet payload, folding the top-level
 * `usage_loaded` flag onto the snippet — same fold the Ember adapter
 * applied. Kept here (rather than at the populate call site) so the
 * cast lives next to the entity that declares `usage_loaded` instead
 * of leaking through `parseSnippet`'s wire-type signature.
 */
export function parseSnippetWithUsageLoaded(raw: SnippetsSnippetWithUsage, usageLoaded: boolean): Snippet {
  return snippet.parse({ ...raw, usage_loaded: usageLoaded });
}

/**
 * Merge a usage-summary response onto an existing snippet. Used by the
 * `getSnippetUsage` paginated endpoint, which returns only the usage
 * fields and not the snippet's core attributes.
 */
export function mergeSnippetUsage(existing: Snippet, summary: SnippetsSnippetUsageSummary): Snippet {
  return {
    ...existing,
    message_item_ids: summary.message_item_ids,
    in_use: summary.in_use,
  };
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions                         */
/* ------------------------------------------------------------------------- */

export const SnippetUsageCountType = {
  Campaigns: 'campaigns',
  Broadcasts: 'broadcasts',
  Transactional: 'transactional',
} as const;
export type SnippetUsageCountType = (typeof SnippetUsageCountType)[keyof typeof SnippetUsageCountType];

export type SnippetUsageCounts = Record<SnippetUsageCountType, number>;

/**
 * Count distinct campaigns, broadcasts, and transactional messages
 * referencing this snippet, derived from its sideloaded template items.
 * Mirrors the Ember `Snippet#usageCounts` getter.
 */
/** @public */
export function snippetUsageCounts(items: readonly SnippetMessageItem[]): SnippetUsageCounts {
  const campaigns = new Set<string>();
  const broadcasts = new Set<string>();
  const transactional = new Set<string>();
  for (const item of items) {
    if (item.campaign_id != null) {
      campaigns.add(item.campaign_id);
    } else if (item.newsletter_id != null) {
      broadcasts.add(item.newsletter_id);
    } else if (item.transactional_message_id != null) {
      transactional.add(item.transactional_message_id);
    }
  }
  return {
    [SnippetUsageCountType.Campaigns]: campaigns.size,
    [SnippetUsageCountType.Broadcasts]: broadcasts.size,
    [SnippetUsageCountType.Transactional]: transactional.size,
  };
}
